import "systemjs/dist/system.min.js";
import { createApp, effectScope, watch, type App as VueApp } from "vue";
import * as singleSpa from "single-spa";
import App from "@/shell/App.vue";
import { pinia } from "@/plugins/pinia";
import { createShellVuetify } from "@/plugins/vuetify";
import { i18n } from "@/i18n";
import { applyLocale, detectLocale } from "@/i18n/locale";
import { applyTenantDefaultTheme, bindSessionAdopter, installGlobalState } from "@/state/globalState";
import { applyBranding } from "@/app/branding";
import { APP_BRANDING, APP_HOME_PAGE, APP_HOME_PATH, APP_MODULES, APP_NAME, APP_SLUG, appImportMap, isDevHost } from "@/app";
import { applyDevImportMapOverrides, injectImportMap, registerTenantModules } from "@/spa/registry";
import { decideHomeRedirect, decideRouteGuard } from "@/nav/routeGuard";
import { routeUnderPrefix } from "@/nav/navFilter";
import { isPublicPath } from "@/nav/publicPaths";
import { isShellOwnedPath } from "@/nav/shellRoutes";
import { applyPwaManifest } from "@/pwa/manifest";
import { watchInstallPrompt } from "@/pwa/install";
import { watchForNewVersion } from "@/pwa/newVersion";
import { registerServiceWorker } from "@/pwa/serviceWorker";
import { useSessionStore } from "@/stores/session";
import { consumeReturnTo, listenForSessionGone, rememberReturnTo } from "@/auth/sessionGone";
import { useOrganisationStore } from "@/stores/organisation";
import { useScopeStore } from "@/stores/scopes";
import { useProductionStore } from "@/stores/production";
// 🔴 The kit's stylesheet, for `PinkCompanyBadge` in the switcher (D164 rule 5): the sidebar draws before any
// module mounts, and a module's own import of it is too late. Only `:root` tokens and `.pk-*` classes.
import "@pinksy/ui/ui.css";
import "@/styles/standalone.css";

function hideBootSplash(): void {
  document.getElementById("boot-splash")?.remove();
}

/** What a boot leaves behind: the mounted app, and one call that tears it all down (tests). */
export interface ShellBoot {
  app: VueApp;
  stop(): void;
}

/**
 * The boot sequence: global state → locale → branding/theme → mount →
 * (installability, service worker, import map) → resume a held session →
 * start the modules once ready. `main.ts` calls this once.
 *
 * 🔴 **NOTHING IS ASKED OF THE SERVER BEFORE THE FIRST SCREEN** (D118, PNK-121). The app used to
 * open by fetching `GET /tenant-apps/resolve?host=<hostname>` and could not draw its own chrome
 * until that answered — which is why three subsystems existed and are now gone: a per-hostname
 * localStorage splash record to paint the brand during the wait, an automatic-retry counter, and an
 * Unavailable screen for a resolve that failed or came back PROVISIONING. `pinksyApp()` is a
 * synchronous read of `src/app/`, so the wait, and everything built to cover it, is not there.
 */
export async function boot(): Promise<ShellBoot> {
  const globalState = installGlobalState();
  // The locale is decided BEFORE the first screen: `?locale=` → the remembered
  // choice → the browser's languages → en-ZA (D4).
  applyLocale(
    detectLocale({
      search: window.location.search,
      persisted: globalState.getState().locale,
      navigatorLanguages: navigator.languages,
    }),
  );
  // Registered BEFORE anything awaits: `beforeinstallprompt` fires once, on
  // Chromium's own schedule, and cannot be recovered if it is missed.
  watchInstallPrompt();

  const app = createApp(App);
  app.use(pinia);
  app.use(i18n);
  // Everything reactive this boot sets up lives in one scope, so `stop()`
  // can end it — the status watcher below outlives the app otherwise.
  const scope = effectScope();
  const teardown: Array<() => void> = [];
  let stopped = false;
  const handle: ShellBoot = {
    app,
    stop() {
      stopped = true;
      for (const undo of teardown.splice(0)) undo();
      scope.stop();
      app.unmount();
    },
  };

  // Read, not fetched. `index.html` already paints Pinksy's own splash statically, so there is no
  // gap between the first paint and knowing what to draw.
  applyBranding(APP_NAME, APP_BRANDING);
  const vuetify = createShellVuetify(APP_BRANDING);
  // One theme for chrome AND module: the global state (what the hosted module
  // reads) decides, seeded from the tenant on a first visit.
  vuetify.theme.change(applyTenantDefaultTheme(globalState, APP_BRANDING.defaultTheme));
  app.use(vuetify);

  const devHost = isDevHost(window.location.hostname, import.meta.env.DEV);
  const session = useSessionStore(pinia);

  // ── A MODULE'S 401 IS THE SHELL'S TO ANSWER (PNK-83) ──────────────────────
  // 🔴 A hosted module that gets a 401 announces `pinksy:session-gone` and throws; it never decides
  // what a 401 means. This is where it is decided: the session is over, so sign out — which unmounts
  // every module and shows sign-in — remember the page the person was on, and once they are in
  // again put them back on it. A screen that printed "invalid passport session: bad_signature" over
  // "No artists yet" was a module deciding, badly, and that is what this replaces.
  const gone = listenForSessionGone(() => {
    if (session.status === "signed-out") return;
    rememberReturnTo(window.location.pathname, window.location.search);
    session.signOut();
  });
  teardown.push(gone.stop);
  scope.run(() =>
    watch(
      () => session.status,
      (status) => {
        if (status !== "ready") return;
        // A new session may lose itself too: the next 401 is a new announcement.
        gone.reset();
        const back = consumeReturnTo();
        if (back !== null && back !== `${window.location.pathname}${window.location.search}`) singleSpa.navigateToUrl(back);
      },
    ),
  );
  session.setApp();
  // A module that obtains a session on its own (the claim module's link session, P-09) hands it in here; the
  // shell verifies it like any other and goes live.
  bindSessionAdopter(globalState, (token) => session.establishSession(token));

  // ── The PRODUCTION CONTEXT (D32) ────────────────────────────────────────
  // 🔴 The SHELL owns reading `?production=<uuid>`; a module must not have to. It is read on every
  // routing event and every popstate, and published on the global state beside the user — so a link,
  // a back button and a fresh tab all put the same production in front of every mounted module.
  // Registered BEFORE `singleSpa.start()`: a module mounting on this navigation must find the context
  // already there, not one event later.
  const production = useProductionStore(pinia);
  const syncProduction = (): void => production.syncFromLocation(window.location.search);
  window.addEventListener("single-spa:before-routing-event", syncProduction);
  window.addEventListener("popstate", syncProduction);
  teardown.push(() => {
    window.removeEventListener("single-spa:before-routing-event", syncProduction);
    window.removeEventListener("popstate", syncProduction);
  });
  scope.run(() =>
    watch(
      () => session.status,
      (status) => {
        if (status !== "ready") {
          // A sign-out ends the context with the session: the next person at this browser starts
          // outside every production, whatever the URL still says.
          production.clear();
          return;
        }
        // Now there is a token to ask with. The id was already read; this is what fetches the NAME —
        // and a production named in the URL of a cold load has been waiting for exactly this. A boot
        // that is not inside a production asks for nothing at all. The standing is asked AGAIN: one
        // fired in the gap between sign-out and this session was answered for nobody.
        syncProduction();
        production.reaskStanding();
      },
      { immediate: true },
    ),
  );

  // ── WHICH COMPANY the session is acting for (D37, D41) ──────────────────
  // 🔴 The list is fetched ONCE a session is live and re-settled against it, rather than trusted from
  // storage: a standing that changed while this tab was closed would otherwise come back as a context
  // the gate refuses on every single request, and the person would meet it as "everything is broken"
  // instead of as "that company is suspended".
  //
  // A sign-out clears it for the same reason the production is cleared: the next person at this
  // browser starts from their own list, not the last person's company.
  const organisation = useOrganisationStore(pinia);
  scope.run(() =>
    watch(
      () => session.status,
      (status) => {
        if (status !== "ready") {
          // 🔴 **ONLY A SESSION THAT ENDED — never one that has not started yet.**
          //
          // This watcher is `immediate`, and a cold boot's first run lands here: the store opens on
          // `"resolving"`, which is not `"ready"`. Clearing there wiped the remembered choice out of
          // sessionStorage BEFORE `settleChoice()` could read it, so step 1 of that method — "a person
          // who reloads is in the same place they were" — was unreachable, and every reload silently
          // moved the person to the alphabetically-first company. Caught in Chrome (D49), not by the
          // suite: a mocked store reloads nothing, so nothing there can see it.
          //
          // `ready` is the distinction, and it is the honest one: it is false until a list has been
          // answered, so this asks "was there something to end?" rather than enumerating the seven
          // statuses that are not `"ready"` and getting one of them wrong later.
          if (organisation.ready) organisation.clear();
          return;
        }
        void organisation.load();
      },
      { immediate: true },
    ),
  );

  // 🔴 The production's record and the switcher's list are a company's questions (`productions:read`): the production
  // store asks them only once a company is known to be held, so a person in no company opening her own show is not
  // refused on every visit (found live as Amalia, 2026-10-02). Her standing is asked regardless.
  scope.run(() =>
    watch(
      () => (!organisation.ready ? "unknown" : organisation.chosen === null ? "none" : "held"),
      (company) => production.setCompany(company),
      { immediate: true },
    ),
  );

  // ── 🔴 WHAT THEY MAY DO THERE (D54) ─────────────────────────────────────
  // Scopes belong to a person IN a company, so this follows the ORGANISATION rather than the session:
  // a switch changes the answer, and a switcher that changed the data but not the doors would leave
  // the previous company's rail on screen. Cleared on sign-out with everything else.
  const scopes = useScopeStore(pinia);
  scope.run(() =>
    watch(
      () => organisation.chosenId,
      (chosenId) => {
        if (session.status !== "ready") {
          scopes.clear();
          return;
        }
        void scopes.load(chosenId);
      },
      { immediate: true },
    ),
  );
  scope.run(() =>
    watch(
      () => session.status,
      (status) => {
        // 🔴 Only a session that ENDED, for the reason D49 records: this watcher is `immediate`, and
        // clearing on a cold boot's "resolving" would be clearing something that was never loaded.
        if (status !== "ready") {
          if (scopes.known) scopes.clear();
          return;
        }
        void scopes.load(organisation.chosenId);
      },
      { immediate: true },
    ),
  );

  app.mount("#app");
  hideBootSplash();

  // Installability. Not awaited — the manifest only decides whether the browser OFFERS to install
  // the app, so drawing icons must never sit in front of the sign-in path.
  void applyPwaManifest({ name: APP_NAME, branding: APP_BRANDING });
  // Not awaited: the worker earns installability and caches the shell, and neither is on the path
  // to the sign-in screen. It no longer serves a cached resolve answer cache-first (F-13): there is
  // no resolve call, and the app description ships inside the bundle the worker already caches.
  const registration = registerServiceWorker({ slug: APP_SLUG });
  // 🔴 A tab on a production desk stays open for weeks, so a build that ships on Monday would sit
  // installed-and-waiting until the browser is restarted. The rail's NEW VERSION pill is how the
  // person finds out and how they choose the moment (pwa/newVersion.ts).
  void registration.then((worker) => {
    if (worker === null) return;
    teardown.push(watchForNewVersion(worker));
  });

  const imports = appImportMap().imports;
  if (imports.length) {
    // `?mfe=<key>=<url>` — a local module build in place of the served one.
    // Dev hosts only: `?mfe=` must never let a visitor swap a module out on a live domain.
    injectImportMap(devHost ? applyDevImportMapOverrides(imports) : imports);
  }

  // A token this tab already holds (auth/session.ts) resumes the session
  // silently once it verifies against the Passport's keys; a fresh visitor
  // lands on Login.vue and waits for a click. Not awaited: the status watcher
  // below starts the modules whenever the session becomes ready.
  void session.resumeFromToken();

  let modulesStarted = false;
  /**
   * 🔴 The deferral is deliberate and it is all that is left of a larger thing. This used to await
   * `controlled` — a promise that, for a tenant whose slug was `passport`, waited (bounded) for the
   * service worker to take control so every chunk a module pulled was cached for the next launch.
   * That tenant was deleted by PNK-106 and migration 044, so the branch had been resolving
   * `Promise.resolve(false)` on every boot: an immediate resolve, which is a microtask hop.
   *
   * The hop stays rather than calling `startModules()` inline, because the caller runs inside the
   * session watcher and starting single-spa synchronously from there reorders the first navigation
   * against the organisation the shell has just chosen (D48, and the bug in the block below).
   * Deleting dead code should not quietly change when the modules start.
   */
  const startModulesOnce = () => {
    if (modulesStarted) return;
    modulesStarted = true;
    queueMicrotask(() => {
      if (!stopped) startModules();
    });
  };
  /**
   * 🔴 **A MODULE MUST NOT FETCH BEFORE THE SHELL HAS CHOSEN AN ORGANISATION** (D44, D48).
   *
   * `X-Organisation` is what layer 4 scopes on, and the shell learns it from `GET /me/organisations`
   * — an answer that arrives a moment AFTER the session goes ready. Starting the modules on the
   * session alone meant a module's first request went out with no organisation header, so the gate
   * resolved no scopes and layer 5 refused it. Caught only by driving the real Desk: every
   * API-level test sends the header, because a test knows what it is testing.
   *
   * The Desk then showed a member *"this action needs 'productions:read', which you do not hold in
   * this organisation"* on a screen they hold exactly that scope for. A refusal that is TRUE of the
   * request and false about the person is the worst kind, because it sends them to an administrator
   * to fix something that is not broken.
   *
   * So readiness is BOTH: a verified session, and an organisation settled — or a definite answer that
   * there is none, which is `ready` with nothing chosen and a real state (a person who belongs to no
   * live company). It is not "wait for a choice", it is "wait for the question to be answered".
   */
  const contextIsSettled = (): boolean => session.status === "ready" && organisation.ready;

  const startModulesOnceReady = () => {
    if (contextIsSettled()) startModulesOnce();
  };

  /**
   * 🔴 A module may only be mounted while somebody is signed in. single-spa decides that from the
   * PATH alone, so without this half a sign-out changes no route, nothing unmounts, and the next
   * person at the same browser reads the last person's screen — proved on this dev stack: Thandi
   * signed out, Pieter signed in, and Pieter, whose `/me/organisations` is empty, was looking at
   * Pinksy Pictures (Pty) Ltd with an "Anchor this company" button under it. The API was never
   * wrong; the mounted module was stale, and a production office is exactly that shared machine.
   *
   * A public path is the deliberate exception: `/l/<token>` (nav/publicPaths.ts) is served
   * signed-out, and the claim module mounts there with no session at all (P-09).
   *
   * 🔴 **AND THE ORGANISATION, for the same reason `startModulesOnceReady` waits for it** (D48).
   *
   * This asked only about the SESSION, and `contextIsSettled()` above guards only the FIRST start —
   * `startModulesOnce` latches and single-spa is running from then on, so after the first boot this
   * predicate is the ONLY thing deciding whether a module may mount. A sign-out clears the
   * organisation with the session; the sign-in that follows remounted the module the instant the
   * session went ready, with `GET /me/organisations` still in flight and nothing on the host state
   * to read. The module's first request went out with no `X-Organisation`, the gate resolved no
   * scopes, and the person was greeted on their own screen with *"this action needs
   * 'productions:read', which you do not hold in this organisation"* — Celia, at Infinity Films,
   * holding `productions:admin`. Same bug as D48 and the same fix; the boot gate simply was not the
   * only door into it.
   */
  const modulesMayMount = (): boolean =>
    contextIsSettled() || isPublicPath(window.location.pathname, APP_MODULES);

  const startModules = () => {
    registerTenantModules(APP_MODULES, modulesMayMount);
    singleSpa.start();

    // `activeWhen` is only re-read when single-spa reroutes, and neither a sign-out nor an
    // organisation settling is a navigation. 🔴 BOTH halves of `modulesMayMount` are watched, or the
    // half that is not leaves the module unmounted until the person happens to click something.
    scope.run(() => watch([() => session.status, () => organisation.ready], () => void singleSpa.triggerAppChange()));

    // 🔴 On `teardown`, not merely added. A `stop()` that leaves this behind leaves a guard bound to
    // a DEAD boot's tenant listening on the live window — it outlives the app it belongs to and
    // redirects on somebody else's routing event. In the browser there is one boot per page load so
    // it never bit; under test, where boots come and go, it made `boot.test.ts` fail about one run
    // in four with a `navigateToUrl` nobody in that test had asked for.
    const onRouting = (): void => {
      const redirectTo = decideRouteGuard(window.location.pathname, window.location.search, APP_MODULES);
      if (redirectTo) singleSpa.navigateToUrl(redirectTo);
    };
    window.addEventListener("single-spa:before-routing-event", onRouting);
    teardown.push(() => window.removeEventListener("single-spa:before-routing-event", onRouting));

    const path = window.location.pathname;
    // Overlays match every path by DESIGN — that must NEVER count as "already
    // inside a module" for landing-page purposes, or the presence of an
    // overlay silently disables the home redirect entirely.
    const insideAModule = APP_MODULES.some((m) => !m.overlay && routeUnderPrefix(path, m.pathPrefix));
    // 🔴 …and the SHELL's own paths, which are under no module prefix BY DESIGN and so looked
    // exactly like an unknown path to the test above. `/security` was bounced on every reload,
    // and `/r/<token>` — a reset link opened by someone who still has a session somewhere —
    // was thrown away in silence. `nav/shellRoutes.ts` has the whole account. (PNK-145)
    if (!insideAModule && !isShellOwnedPath(path)) {
      const home = decideHomeRedirect(APP_HOME_PATH, APP_MODULES, APP_HOME_PAGE.enabled);
      if (home) singleSpa.navigateToUrl(home);
    }
  };

  // A public path — the link, `/l/<token>` (nav/publicPaths.ts) — is served signed-out: the module mounted on it
  // starts now, with no session, and the session it establishes (adoptSession) draws the chrome around it.
  if (isPublicPath(window.location.pathname, APP_MODULES)) startModulesOnce();

  // 🔴 BOTH halves of the context feed one gate, and either may land last: a resumed session with the
  // organisation list still in flight, or a fresh sign-in where the session is the slower of the two.
  scope.run(() => watch(() => session.status, startModulesOnceReady, { immediate: true }));
  scope.run(() => watch(() => organisation.ready, startModulesOnceReady));
  return handle;
}
